import { createAsyncThunk } from '@reduxjs/toolkit';
import { httpHelperV2 as httpRequest } from 'v2/services/httpHelper';
import { isFailureJobStatus } from 'v1/quotes-tender/components/page/package/analyse/packageJobScope';

const fetchTenderInsights = createAsyncThunk(
    'tenderInsights/fetch',
    async ({ tenderId, documentId = null }) => {
        const url = documentId
            ? `ai/tender-insights/${tenderId}?document_id=${documentId}`
            : `ai/tender-insights/${tenderId}`;

        const response = await httpRequest({
            url,
            method: 'GET',
        });

        return {
            tenderId,
            data: response,
            timestamp: new Date().toISOString(),
        };
    }
);

const createTenderInsights = createAsyncThunk(
    'tenderInsights/create',
    async ({ tenderId, documentId = null }) => {
        const url = documentId
            ? `ai/tender-insights/${tenderId}?document_id=${documentId}`
            : `ai/tender-insights/${tenderId}`;

        const response = await httpRequest({
            url,
            method: 'POST',
        });

        return {
            tenderId,
            data: response,
        };
    }
);

const hasValidInsights = (data) => {
    if (!data) return false;

    const insightsData = data.result?.data || data.data || data;

    if (!insightsData) return false;

    const sections = [
        insightsData.headline_dates,
        insightsData.payment_terms,
        insightsData.contractual_risks,
        insightsData.warnings_notes,
        insightsData.insurances,
        insightsData.scope_attendances,
        insightsData.dayworks,
        insightsData.health_safety_site_rules,
        insightsData.tender_submission_requirements,
        insightsData.scope_of_works,
    ];

    return sections.some((section) => {
        if (!section) return false;
        return typeof section === 'object' && Object.keys(section).length > 0;
    });
};

const applyTenderInsightsFulfilled = (state, tenderId, data, timestamp) => {
    if (isFailureJobStatus(data?.status)) {
        state.insights[tenderId] = {
            ...state.insights[tenderId],
            status: 'error',
            error: null,
            errorPayload: data,
            data: null,
            files: null,
        };
        return;
    }

    if (!hasValidInsights(data)) {
        if (data?.status === 'PENDING' || data?.status === 'STARTED') {
            state.insights[tenderId] = {
                ...state.insights[tenderId],
                status: 'PENDING',
                error: null,
                errorPayload: null,
            };
            return;
        }

        state.insights[tenderId] = {
            ...state.insights[tenderId],
            status: 'error',
            error: 'No insights were generated from the tender document.',
            errorPayload: null,
            data: null,
            files: null,
        };
        return;
    }

    const insightsData = data.result?.data || data.data || data;
    const files = data.files || data.result?.files || null;

    state.insights[tenderId] = {
        data: insightsData,
        files,
        timestamp: timestamp ?? data.completed_at,
        status: 'success',
        error: null,
        errorPayload: null,
    };
};

export default {
    [fetchTenderInsights.pending]: (state, { meta }) => {
        const { tenderId } = meta.arg;
        state.insights[tenderId] = {
            ...state.insights[tenderId],
            status: 'loading',
            error: null,
            errorPayload: null,
        };
    },
    [fetchTenderInsights.fulfilled]: (state, { payload }) => {
        const { tenderId, data, timestamp } = payload;
        applyTenderInsightsFulfilled(state, tenderId, data, timestamp);
    },
    [fetchTenderInsights.rejected]: (state, { meta, error }) => {
        const { tenderId } = meta.arg;
        state.insights[tenderId] = {
            ...state.insights[tenderId],
            status: 'error',
            error: error.message || 'Failed to fetch tender insights',
            errorPayload: null,
        };
    },
    [createTenderInsights.pending]: (state, { meta }) => {
        const { tenderId } = meta.arg;
        state.insights[tenderId] = {
            ...state.insights[tenderId],
            status: 'creating',
            error: null,
            errorPayload: null,
        };
    },
    [createTenderInsights.fulfilled]: (state, { payload }) => {
        const { tenderId, data, timestamp } = payload;
        applyTenderInsightsFulfilled(state, tenderId, data, timestamp);
    },
    [createTenderInsights.rejected]: (state, { meta, error }) => {
        const { tenderId } = meta.arg;
        state.insights[tenderId] = {
            ...state.insights[tenderId],
            status: 'error',
            error: error.message || 'Failed to create tender insights',
            errorPayload: null,
        };
    },
};

export { fetchTenderInsights, createTenderInsights };
